import React from "react";
import { Clock, Truck, Package, CheckCircle } from "lucide-react";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient } from "../../../apiclient/apiclient";
//import SalesSummary from "./SalesSummary";
import socket from "../../../socket/socketClient";

/* ===============================
   🔹 SALES ACTIVITY CARD
================================ */
const SalesCard = ({ value, unit, label, icon: Icon, color, onClick }) => {
  return (
    <div
      onClick={onClick}
      className="bg-white px-4 py-5 border-r border-b border-slate-200 transition text-center cursor-pointer hover:bg-gray-50 flex flex-col items-center justify-center min-h-[110px]"
    >
      {/* ICON */}
      <div
        className={`mx-auto mb-1 flex items-center justify-center 
        w-8 h-8 rounded-full ${color} bg-opacity-15`}
      >
        <Icon size={18} className={color} />
      </div>

      {/* VALUE */}
      <div className="text-2xl font-bold leading-tight">{value}</div>

      {/* UNIT
      <div className="text-xs text-gray-500 leading-none">
        {unit}
      </div> */}

      {/* LABEL */}
      <div className="mt-1 text-[11px] text-gray-600 font-bold uppercase tracking-wide">
        {label}
      </div>
    </div>
  );
};

/* ===============================
   🔹 INVENTORY ROW
================================ */
const InventoryRow = ({ label, value, dot }) => {
  return (
    <div className="flex items-center justify-between bg-white rounded-md px-3 py-2 border border-gray-200">
      <div className="flex items-center gap-2">
        <span className={`h-2 w-2 rounded-full ${dot}`} />
        <span className="text-xs text-gray-600">{label}</span>
      </div>
      <span className="text-sm font-semibold">{value}</span>
    </div>
  );
};

/* ===============================
   🔹 MAIN COMPONENT
================================ */

const StatsCards = () => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const handleStatusClick = (status) => {
    navigate(`/orders/view-order?status=${status}`);
  };

  useEffect(() => {
    async function loadSummary() {
      try {
        setLoading(true);
        const res = await apiClient.get("/summary?rangeDays=7");
        console.log("[Dashboard.summary] response.orders:", res.data.orders);
        setSummary(res.data);
      } catch (err) {
        console.error("Failed to load dashboard summary", err);
      } finally {
        setLoading(false);
      }
    }

    // initial load
    loadSummary();

    // Keep cards realtime via socket events
    const events = [
      "order_created",
      "order_updated",
      "order_status_updated",
      "order_deleted",
      "order_collected",
    ];
    events.forEach((ev) => socket.on(ev, loadSummary));

    // Also reload summary when socket reconnects
    socket.on("connect", loadSummary);

    return () => {
      events.forEach((ev) => socket.off(ev, loadSummary));
      socket.off("connect", loadSummary);
    };
  }, []);

  // derive values with safe fallbacks
  const pending = summary?.orders?.pending ?? 0;
  // Show shipped only (do not mix with processing)
  const shipped = summary?.orders?.shipped ?? 0;
  const delivered = summary?.orders?.delivered ?? 0;
  const completed = summary?.orders?.completed ?? 0;

  const totalQty = summary?.inventory?.totalQty ?? 0;
  const lowStock = summary?.inventory?.lowStock ?? 0;
  const outOfStock = summary?.inventory?.outOfStock ?? 0;

  return (
    <div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 border-l border-slate-200">
        {/* ================= SALES ACTIVITY CARDS ================= */}
        <SalesCard
          value={loading ? "..." : pending.toLocaleString()}
          label="To Be Packed"
          icon={Clock}
          color="text-blue-600"
          onClick={() => handleStatusClick("pending")}
        />
        <SalesCard
          value={loading ? "..." : shipped.toLocaleString()}
          label="To Be Shipped"
          icon={Truck}
          color="text-red-500"
          onClick={() => handleStatusClick("shipped")}
        />
        <SalesCard
          value={loading ? "..." : delivered.toLocaleString()}
          label="To Be Delivered"
          icon={Package}
          color="text-green-600"
          onClick={() => handleStatusClick("delivered")}
        />
        <SalesCard
          value={loading ? "..." : completed.toLocaleString()}
          label="Completed"
          icon={CheckCircle}
          color="text-indigo-600"
          onClick={() => handleStatusClick("completed")}
        />

        {/* ================= INVENTORY SUMMARY - QUICK VIEW ================= */}
        <div
          onClick={() => navigate("/reports/inventory-reports")}
          className="bg-white px-4 py-5 border-r border-b border-slate-200 transition text-center cursor-pointer hover:bg-gray-50 flex flex-col items-center justify-center min-h-[110px]"
        >
          <div className="mx-auto mb-1 flex items-center justify-center w-8 h-8 rounded-full bg-green-500 bg-opacity-15">
            <Package size={18} className="text-green-600" />
          </div>
          <div className="text-2xl font-bold leading-tight">
            {loading ? "..." : totalQty.toLocaleString()}
          </div>
          <div className="mt-1 text-[11px] text-gray-600 font-bold uppercase tracking-wide">
            Products In Hand
          </div>
        </div>

        <div
          onClick={() => navigate("/reports/inventory-reports")}
          className="bg-white px-4 py-5 border-r border-b border-slate-200 transition text-center cursor-pointer hover:bg-gray-50 flex flex-col items-center justify-center min-h-[110px]"
        >
          <div className="mx-auto mb-1 flex items-center justify-center w-8 h-8 rounded-full bg-orange-500 bg-opacity-15">
            <Clock size={18} className="text-orange-600" />
          </div>
          <div className="text-2xl font-bold leading-tight">
            {loading ? "..." : lowStock.toLocaleString()}
          </div>
          <div className="mt-1 text-[11px] text-gray-600 font-bold uppercase tracking-wide">
            Low Stock Products
          </div>
        </div>
      </div>
    </div>
  );
};

export default StatsCards;
